/**
 * useProviderPortalAdmin - Lado do gestor do Portal do Prestador (via RPC).
 *
 * Liga/desliga o portal (administradores), consulta, libera e bloqueia o acesso
 * de um prestador. A validação definitiva é feita no banco.
 *
 * RELEASE: 2026-10-13 v1.1.0 - Configurações de chegada ao local (raio, precisão, política, foto).
 * RELEASE: 2026-10-12 v1.0.0 - Versão inicial (Fase 1).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useProviderPortalAdmin');

// Preserva o `this` do cliente (RPC fora da tipagem gerada)
const rpc = (name: string, args: Record<string, unknown>) =>
  (supabase.rpc as unknown as (n: string, a: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>)
    .call(supabase, name, args);

export interface ProviderPortalAccess {
  id: string;
  email: string;
  status: 'pending' | 'active' | 'blocked';
  invited_at: string;
  activated_at: string | null;
  last_login_at: string | null;
}

/** Endereço público do portal do tenant. */
export const providerPortalUrl = (slug: string) => `${window.location.origin}/prestador/${slug}`;

export interface ProviderPortalSettings {
  enabled: boolean; slug: string;
  arrival_radius_m?: number; arrival_max_accuracy_m?: number;
  arrival_out_of_radius?: 'justify' | 'block'; arrival_photo_required?: boolean;
}

export function useProviderPortalSettings() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['provider-portal-settings', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await rpc('get_provider_portal_settings', { p_tenant_hint: tenantId ?? null });
      if (error) throw new Error(error.message);
      return (data ?? { enabled: false, slug: '' }) as ProviderPortalSettings;
    },
  });
  const toggle = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { error } = await rpc('update_provider_portal_settings', { p_enabled: enabled, p_tenant_hint: tenantId ?? null });
      if (error) throw new Error(error.message);
    },
    onSuccess: (_d, enabled) => {
      qc.invalidateQueries({ queryKey: ['provider-portal-settings'] });
      toast.success(enabled ? 'Portal do Prestador habilitado' : 'Portal do Prestador desabilitado');
    },
    onError: (e: Error) => toast.error(e.message),
  });
  // Chegada ao local: só os campos informados são alterados; validação definitiva na RPC.
  const updateArrival = useMutation({
    mutationFn: async (v: { radius?: number; accuracy?: number; outOfRadius?: 'justify' | 'block'; photoRequired?: boolean }) => {
      const { error } = await rpc('update_provider_portal_settings', {
        p_arrival_radius_m: v.radius ?? null, p_arrival_max_accuracy_m: v.accuracy ?? null,
        p_arrival_out_of_radius: v.outOfRadius ?? null, p_arrival_photo_required: v.photoRequired ?? null,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['provider-portal-settings'] }); toast.success('Configuração de chegada salva'); },
    onError: (e: Error) => { logger.error('Falha ao salvar chegada', e); toast.error(e.message); },
  });
  return { settings: query.data, isLoading: query.isLoading, toggle, updateArrival };
}

export function useProviderPortalAccess(personId: string | null) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  const qc = useQueryClient();
  const key = ['provider-portal-access', tenantId, personId];
  const query = useQuery({
    queryKey: key,
    enabled: !!tenantId && !!personId,
    queryFn: async () => {
      const { data, error } = await rpc('get_provider_portal_access', { p_person_id: personId, p_tenant_hint: tenantId ?? null });
      if (error) throw new Error(error.message);
      return (data ?? null) as ProviderPortalAccess | null;
    },
  });
  const onError = (e: Error) => { logger.error('Erro no acesso do portal', { error: e.message }); toast.error(e.message); };
  const invite = useMutation({
    mutationFn: async (email: string) => {
      const { error } = await rpc('invite_provider_portal_user', { p_person_id: personId, p_email: email.trim().slice(0, 255), p_tenant_hint: tenantId ?? null });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success('Acesso liberado'); },
    onError,
  });
  const setBlocked = useMutation({
    mutationFn: async (blocked: boolean) => {
      const { error } = await rpc('set_provider_portal_user_blocked', { p_person_id: personId, p_blocked: blocked, p_tenant_hint: tenantId ?? null });
      if (error) throw new Error(error.message);
    },
    onSuccess: (_d, b) => { qc.invalidateQueries({ queryKey: key }); toast.success(b ? 'Acesso bloqueado' : 'Acesso desbloqueado'); },
    onError,
  });
  return { access: query.data, isLoading: query.isLoading, invite, setBlocked };
}
